import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseCreateDailyToolMutation, UseDailyToolMutation } from '../../../../hooks/mutations/useDailyToolMutation';
import { useToolsQuery } from '../../../../hooks/queries/useToolsQuery';
import { DailyTool } from '../../../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getMaterialStateInfos, materialState } from '../../../../utils/enums';
import DropDown, { OptionType } from '../../../DropDown';
import ToggleLine from '../../../ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';
import QuantityToggleGroup from '../../../QuantityToggleGroup';

interface ManageValidationDailyToolModalProps extends ModalProps {
    refetch: () => void;
    dailyTool: DailyTool | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailyToolModal: React.FC<ManageValidationDailyToolModalProps> = ({
    open,
    handleClose,
    refetch,
    dailyTool,
    activities,
    siteName,
}) => {
    const { t } = useTranslation();
    const [currentDailyToolId] = useState(dailyTool?._id || '');
    const [selectedTool, setSelectedTool] = useState({
        label: dailyTool?.toolId.designation || '',
        value: dailyTool?.toolId._id || '',
    });
    const [selectedActivity, setSelectedActivity] = useState({
        label: activities.find((activity) => activity.value === dailyTool?.activityId)?.label || '',
        value: dailyTool?.activityId || '',
    });
    const [isUsed, setIsUsed] = useState<boolean>(dailyTool?.isUsed ?? true);
    const [selectedState, setSelectedState] = useState<{
        label: string;
        value: string;
    }>({
        label: capitalizeFirstLetter(t(getMaterialStateInfos(dailyTool?.state ?? materialState.PRESENT))),
        value: dailyTool?.state.toString() || materialState.PRESENT.toString(),
    });
    const [quantity, setQuantity] = useState<number>(dailyTool?.quantity ?? 1);

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.NEW))), value: materialState.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.PRESENT))), value: materialState.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.REMOVE))), value: materialState.REMOVE.toString() },
    ];

    const { data: dataTools } = useToolsQuery({ queries: { active: true } });

    const { mutate: createDailyTool } = UseCreateDailyToolMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailyTool } = UseDailyToolMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postDailyTool = async () => {
        createDailyTool({
            activityId: selectedActivity.value,
            toolId: selectedTool.value,
            state: parseInt(selectedState.value),
            isUsed,
            quantity
        });
    };

    const patchDailyTool = async () => {
        if (currentDailyToolId)
            updateDailyTool({
                id: currentDailyToolId,
                body: {
                    activityId: selectedActivity.value,
                    toolId: selectedTool.value,
                    state: parseInt(selectedState.value),
                    isUsed,
                    quantity
                },
            });
    };

    return (
        <ModalBody
            closeText={t('cancel')}
            title={t('tool')}
            subTitle={!!currentDailyToolId.length ? t('update') : t('add')}
            description={siteName}
            okText={t('validate')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                !currentDailyToolId.length ? postDailyTool() : patchDailyTool();
            }}
            disabledOk={!selectedTool.value.length || !selectedActivity.value.length || !selectedState.value.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedTool}
                items={
                    dataTools?.map((tool) => ({
                        label: tool.designation,
                        value: tool._id,
                        group: tool.toolTypeId.designation,
                    })) || []
                }
                handleChange={(event, value) => setSelectedTool({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('tool')}
                grouped
                required
            />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => setSelectedState({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('state')}
                required
            />
            <QuantityToggleGroup value={quantity} onChange={(value) => setQuantity(value)} />
        </ModalBody>
    );
};

export default ManageValidationDailyToolModal;
